import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MultipleSelectCheckmarks from "components/Form/MultipleSelectCheckmarks";
import CustomDateTimePicker from "components/Form/CustomDateTimePicker";
import DeleteIcon from "@mui/icons-material/Delete";

import "react-datepicker/dist/react-datepicker.css";
import DynamicAutocomplete from "components/Form/DynamicAutocomplete";
import FileUpload from "components/Form/FileUpload";
import Icon from "@mui/material/Icon";

function buildDefaultsFromSchema(schema) {
  const defaults = {};
  schema.forEach((f) => {
    // your schema uses `value` as the default
    if (f.fieldType === "multiselect" || f.fieldType === "dynamicList") {
      defaults[f.id] = Array.isArray(f.value) ? f.value : [];
    } else if (f.fieldType === "checkbox") {
      defaults[f.id] = f.value !== undefined ? Boolean(f.value) : false;
    } else if (f.fieldType === "file") {
      // For file uploads, initialize as null for single or [] for multiple
      defaults[f.id] = f.multiple ? [] : null;
    } else {
      defaults[f.id] = f.value !== undefined ? f.value : "";
    }
  });
  return defaults;
}

const isFieldShown = (field, formData) => {
  if (field.shownOnForm === undefined) return true;
  if (typeof field.shownOnForm === "function") {
    return field.shownOnForm(formData);
  }
  return Boolean(field.shownOnForm);
};

const isFieldRequired = (field, formData) => {
  if (typeof field.isRequired === "function") {
    return field.isRequired(formData);
  }
  return Boolean(field.isRequired);
};

function validateFromSchema(schema, data) {
  const errors = {};

  for (const f of schema) {
    // Check if field is visible before validating
    const isShown = isFieldShown(f, data);
    const isRequired = isFieldRequired(f, data);

    if (!isShown || !isRequired) continue;

    const v = data[f.id];
    let validate = validateField(f, v, data);
    if (validate.length > 0) {
      errors[f.id] = validate;
    }
  }

  return errors;
}

function validateField(f, value, formData) {
  const v = value ?? "";
  const isRequired = typeof f.isRequired === "function" ? f.isRequired(formData) : f.isRequired;

  // Required checks
  if (isRequired) {
    if (
      (f.fieldType === "textfield" ||
        f.fieldType === "textarea" ||
        f.fieldType === "datetime" ||
        f.fieldType === "date") &&
      (!value || String(value).trim() === "")
    ) {
      return `${f.label} is required.`;
    }
  }

  if (f.fieldType === "select" && isRequired && (value === "" || value == null)) {
    return `${f.label} is required.`;
  }

  if (
    f.fieldType === "multiselect" &&
    isRequired &&
    (!Array.isArray(value) || value.length === 0)
  ) {
    return `${f.label} is required.`;
  }

  if (f.fieldType === "checkbox" && isRequired && !value) {
    return `${f.label} must be checked.`;
  }

  if (f.fieldType === "dynamicList") {
    const list = Array.isArray(value) ? value : [];

    // Check if the whole list is required
    if (isRequired && list.length === 0) {
      return `${f.label} must have at least one entry.`;
    }

    // Check individual cells
    for (let i = 0; i < list.length; i++) {
      for (const col of f.columns) {
        const cellValue = list[i][col.id];
        if (col.isRequired && (cellValue === "" || cellValue === null || cellValue === undefined)) {
          return `Row ${i + 1}: ${col.label} is required.`;
        }
      }
    }
  }

  // Max length
  if (f.maxChar && value && String(value).length > f.maxChar) {
    return `${f.label} must be at most ${f.maxChar} characters.`;
  }

  // Regex pattern (phone, email, etc.)
  if (f.pattern && value && !f.pattern.test(value)) {
    return f.patternMessage || `${f.label} is invalid.`;
  }

  // Add file validation
  if (f.fieldType === "file") {
    if (isRequired && (!value || (f.multiple && value.length === 0) || (!f.multiple && !value))) {
      return `${f.label} is required.`;
    }

    // Validate file size for single file
    if (!f.multiple && value && value instanceof File) {
      if (f.maxSize && value.size > f.maxSize) {
        return `${f.label} must be less than ${formatFileSize(f.maxSize)}.`;
      }
      if (f.accept && !isAcceptedFileType(value.name, f.accept)) {
        return `${f.label} must be of type: ${f.accept}.`;
      }
    }

    // Validate multiple files
    if (f.multiple && Array.isArray(value) && value.length > 0) {
      if (f.maxFiles && value.length > f.maxFiles) {
        return `${f.label} cannot have more than ${f.maxFiles} files.`;
      }
      for (const file of value) {
        if (f.maxSize && file.size > f.maxSize) {
          return `${f.label} file "${file.name}" exceeds ${formatFileSize(f.maxSize)} limit.`;
        }
        if (f.accept && !isAcceptedFileType(file.name, f.accept)) {
          return `${f.label} file "${file.name}" must be of type: ${f.accept}.`;
        }
      }
    }
  }

  return "";
}

// Helper function for file size formatting
function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Helper function to check accepted file types
function isAcceptedFileType(filename, acceptPattern) {
  if (!acceptPattern) return true;
  const extensions = acceptPattern.split(",").map((ext) => ext.trim().toLowerCase());
  const fileExt = "." + filename.split(".").pop().toLowerCase();
  return extensions.some((ext) => ext === fileExt || ext === "." + fileExt);
}

// Component for autocomplete field to avoid hooks rules violation
const AutocompleteField = React.memo(
  ({ f, data, deps, hasError, hasSuccess, errMsg, onFieldDataChange }) => {
    const optionsList = typeof f.options === "function" ? f.options(deps) || [] : [];
    const textValue = f.textValue || "name";
    const valueKey = f.valueKey || "id";
    const currentId = data[f.id];
    const isMultiple = !!f.multiple;

    const selectedValue = useMemo(() => {
      if (!currentId || (isMultiple && Array.isArray(currentId) && currentId.length === 0)) {
        return isMultiple ? [] : null;
      }
      if (isMultiple && Array.isArray(currentId)) {
        return optionsList.filter((opt) =>
          currentId.some((id) => String(id) === String(opt[valueKey]))
        );
      }
      const match = optionsList.find((opt) => String(opt[valueKey]) === String(currentId));
      return match || null;
    }, [currentId, optionsList, valueKey, isMultiple]);

    useEffect(() => {
      if (
        currentId &&
        (!isMultiple ? !selectedValue : selectedValue.length === 0) &&
        !f.loading &&
        typeof f.onSearch === "function"
      ) {
        // If we have IDs but no matching objects in optionsList, fetch them
        f.onSearch(currentId, deps);
      }
    }, [currentId, selectedValue, f, deps, isMultiple]);

    return (
      <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
        <Autocomplete
          key={`${f.id}`}
          options={optionsList}
          value={selectedValue}
          multiple={isMultiple}
          disableCloseOnSelect={isMultiple}
          loading={f.loading || false}
          getOptionLabel={(option) => (option ? option[textValue] || "" : "")}
          isOptionEqualToValue={(option, val) =>
            String(option?.[valueKey]) === String(val?.[valueKey])
          }
          onChange={(event, newValue) => {
            if (isMultiple) {
              const ids = newValue ? newValue.map((v) => v[valueKey]) : [];
              onFieldDataChange(f, ids);
            } else {
              onFieldDataChange(f, newValue ? newValue[valueKey] : "");
            }
          }}
          onInputChange={(e, val, reason) => {
            if (reason === "input" && val.length >= 3) {
              if (typeof f.onSearch === "function") {
                f.onSearch(val, deps);
              }
            } else if (reason === "clear") {
              f.onSearch(val, deps);
            }
          }}
          renderInput={(params) => (
            <MDInput
              {...params}
              label={f.label}
              fullWidth
              error={hasError}
              success={hasSuccess}
              helperText={hasError ? errMsg : ""}
            />
          )}
        />
      </MDBox>
    );
  }
);

AutocompleteField.propTypes = {
  f: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.func,
    textValue: PropTypes.string,
    valueKey: PropTypes.string,
    multiple: PropTypes.bool,
    loading: PropTypes.bool,
    onSearch: PropTypes.func,
  }).isRequired,
  data: PropTypes.object.isRequired,
  deps: PropTypes.object,
  hasError: PropTypes.bool,
  hasSuccess: PropTypes.bool,
  errMsg: PropTypes.string,
  onFieldDataChange: PropTypes.func.isRequired,
};

function SchemaDialogForm({
  open,
  title,
  schema,
  initialRecord,
  saving,
  onClose,
  onSubmit,
  deps = {},
  submitButtonText,
}) {
  const defaults = useMemo(() => buildDefaultsFromSchema(schema), [schema]);
  const [data, setData] = useState({ ...defaults });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;

    // 1. Sync local data state with what's coming from parent
    setData((prev) => ({
      ...defaults,
      ...prev,
      ...(initialRecord || {}),
    }));

    // 2. Generic check for externalErrors inside initialRecord
    if (initialRecord?.externalErrors) {
      const extErrors = initialRecord.externalErrors;

      setFieldErrors((prev) => ({
        ...prev,
        ...extErrors,
      }));

      // Mark fields as touched so the red error state renders immediately
      setTouched((prev) => {
        const newTouched = { ...prev };
        Object.keys(extErrors).forEach((key) => {
          newTouched[key] = true;
        });
        return newTouched;
      });
    }

    setError("");
  }, [open, defaults, initialRecord]);

  const setField = (id, value) => {
    setData((prev) => ({ ...prev, [id]: value }));

    const fieldSchema = schema.find((x) => x.id === id);
    if (!fieldSchema) return;

    const errorMsg = validateField(fieldSchema, value, data);

    setFieldErrors((prev) => ({
      ...prev,
      [id]: errorMsg || "",
    }));
  };

  const touch = (id) => setTouched((p) => ({ ...p, [id]: true }));

  const handleSave = async () => {
    const errors = validateFromSchema(schema, data);
    setFieldErrors(errors);

    // mark all VISIBLE fields as touched so errors show
    const touchedAll = {};
    schema.forEach((f) => {
      if (isFieldShown(f, data)) {
        touchedAll[f.id] = true;
      }
    });
    setTouched(touchedAll);

    if (Object.keys(errors).length > 0) {
      setError("Please correct the highlighted fields.");
      return;
    }

    setError("");
    await onSubmit(data);
  };

  const handleSubmitFromOtherButtons = async (onSubmitEvent) => {
    const errors = validateFromSchema(schema, data);
    setFieldErrors(errors);

    // mark all VISIBLE fields as touched so errors show
    const touchedAll = {};
    schema.forEach((f) => {
      if (isFieldShown(f, data)) {
        touchedAll[f.id] = true;
      }
    });
    setTouched(touchedAll);

    if (Object.keys(errors).length > 0) {
      setError("Please correct the highlighted fields.");
      return;
    }

    setError("");
    await onSubmitEvent(data);
  };

  const onFieldDataChange = async (field, value) => {
    touch(field.id);
    setField(field.id, value);

    if (fieldErrors[field.id]) {
      setFieldErrors((prev) => ({ ...prev, [field.id]: "" }));
    }
    setData((prev) => ({ ...prev, [field.id]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        {Object.entries(
          schema
            .filter((f) => isFieldShown(f, data))
            .reduce((acc, field) => {
              const groupName = field.group || "General Information"; // Default group
              if (!acc[groupName]) acc[groupName] = [];
              acc[groupName].push(field);
              return acc;
            }, {})
        ).map(([groupName, fields]) => (
          <MDBox
            key={groupName}
            mb={3}
            p={3}
            sx={{
              backgroundColor: "#f9f9fa",
              border: "1px solid #eee",
              borderRadius: "8px",
              margin: "16px",
            }}
          >
            <MDTypography
              sx={{
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
              color="info"
              textTransform="uppercase"
            >
              {groupName}
            </MDTypography>
            <Grid container spacing={1} sx={{ width: "98%", margin: 0 }}>
              {fields.map((f) => {
                const value = data[f.id] ?? "";
                const isTouched = Boolean(touched[f.id]);
                const errMsg = fieldErrors[f.id] || "";
                const hasError = isTouched && Boolean(errMsg);
                const hasSuccess =
                  (isTouched && !errMsg && String(value).trim() !== "") || undefined;

                if (f.fieldType === "textfield") {
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                        sx={{ gap: 2, flexDirection: { xs: "column", sm: "row" } }}
                      >
                        <MDInput
                          type={f.inputType || "text"}
                          label={f.label}
                          disabled={f.disabled || false}
                          fullWidth={!f.showGenerateButton}
                          sx={{ flex: f.showGenerateButton ? 1 : undefined }}
                          error={hasError}
                          success={hasSuccess}
                          value={data[f.id] ?? ""}
                          onChange={(e) => onFieldDataChange(f, e.target.value)}
                          onKeyDown={(e) => {
                            if (f.onKeyDown) {
                              f.onKeyDown(e, data[f.id], deps, data);
                            }
                          }}
                        />
                        {f.showGenerateButton && (
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            onClick={() => {
                              const randomCode = f.generateCodeFunction();
                              onFieldDataChange(f, randomCode);
                            }}
                            sx={{ flexShrink: 0, alignSelf: "center" }}
                          >
                            {f.generateButtonLabel || "Generate Code"}
                          </MDButton>
                        )}
                      </MDBox>
                      <MDTypography
                        variant="caption"
                        color={hasError ? "error" : "text"}
                        sx={{ mt: 0.5 }}
                      >
                        {hasError ? errMsg : " "}
                      </MDTypography>
                    </MDBox>
                  );
                }

                if (f.fieldType === "password") {
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <MDInput
                        type="password"
                        label={f.label}
                        disabled={f.disabled || false}
                        fullWidth
                        error={hasError}
                        success={hasSuccess}
                        value={data[f.id] ?? ""}
                        onChange={(e) => onFieldDataChange(f, e.target.value)}
                        helperText={fieldErrors[f.id] || ""}
                      />
                    </MDBox>
                  );
                }

                if (f.fieldType === "textarea") {
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <MDInput
                        disabled={f.disabled || false}
                        label={f.label}
                        fullWidth
                        multiline
                        error={hasError}
                        success={hasSuccess}
                        value={data[f.id] ?? ""}
                        onChange={(e) => onFieldDataChange(f, e.target.value)}
                        helperText={fieldErrors[f.id] || ""}
                      />
                    </MDBox>
                  );
                }

                if (f.fieldType === "multiselect") {
                  const optionsList = typeof f.options === "function" ? f.options(deps) || [] : [];
                  const textValue = f.textValue || "name";
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <MultipleSelectCheckmarks
                        key={f.id}
                        label={f.label}
                        value={data[f.id] || []}
                        error={hasError}
                        success={hasSuccess}
                        optionsList={optionsList}
                        onSelection={(e) => onFieldDataChange(f, e)}
                        textValue={textValue}
                        height={100}
                      />
                    </MDBox>
                  );
                }

                if (f.fieldType === "select") {
                  const optionsList = typeof f.options === "function" ? f.options(deps) || [] : [];
                  const textValue = f.textValue || "name";
                  const valueKey = f.valueKey || "id";

                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <FormControl fullWidth>
                        <InputLabel>{f.label}</InputLabel>
                        <Select
                          disabled={f.disabled || false}
                          value={data[f.id] ?? ""}
                          onChange={(e) => onFieldDataChange(f, e.target.value)}
                          error={hasError}
                          success={hasSuccess}
                          input={<OutlinedInput label={f.label} />}
                          sx={{ minHeight: 50 }}
                        >
                          {optionsList.map((opt) => (
                            <MenuItem key={opt[valueKey]} value={opt[valueKey]}>
                              {opt[textValue]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </MDBox>
                  );
                }

                if (f.fieldType === "datetime") {
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <CustomDateTimePicker
                        label={f.label}
                        disabled={f.disabled || false}
                        key={f.id}
                        onDateSelected={(v) => onFieldDataChange(f, v)}
                        success={hasSuccess}
                        error={hasError}
                        helperText={fieldErrors[f.id] || ""}
                        value={data[f.id] || null}
                      />
                    </MDBox>
                  );
                }

                if (f.fieldType === "date") {
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <MDInput
                        type="date"
                        label={f.label}
                        fullWidth
                        error={hasError}
                        success={hasSuccess}
                        value={data[f.id] ?? ""}
                        onChange={(e) => onFieldDataChange(f, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      {hasError && (
                        <MDTypography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {errMsg}
                        </MDTypography>
                      )}
                    </MDBox>
                  );
                }

                if (f.fieldType === "checkbox") {
                  return (
                    <MDBox
                      mt={2}
                      mb={2}
                      key={f.id}
                      display="flex"
                      flexDirection="column"
                      sx={{ width: f.width || "100%" }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={data[f.id] === f.valueMap.y}
                            onChange={(e) => {
                              const val = e.target.checked ? f.valueMap.y : f.valueMap.n;
                              onFieldDataChange(f, val);
                            }}
                          />
                        }
                        label={
                          <MDTypography variant="button" fontWeight="regular" color="text">
                            {f.label}
                          </MDTypography>
                        }
                      />
                      {hasError && (
                        <MDTypography variant="caption" color="error" sx={{ ml: 3 }}>
                          {errMsg}
                        </MDTypography>
                      )}
                    </MDBox>
                  );
                }

                if (f.fieldType === "file") {
                  return (
                    <MDBox mt={2} mb={2} key={f.id} sx={{ width: f.width || "100%" }}>
                      <FileUpload
                        label={f.label}
                        value={data[f.id]}
                        onChange={(fileData) => {
                          onFieldDataChange(f, fileData);
                        }}
                        error={hasError}
                        success={hasSuccess}
                        helperText={errMsg || f.helperText}
                        accept={f.accept}
                        multiple={f.multiple}
                        maxSize={f.maxSize}
                        maxFiles={f.maxFiles}
                        disabled={f.disabled || false}
                        uploading={f.uploading || false}
                        uploadProgress={f.uploadProgress || 0}
                        showDownloadTemplate={f.showDownloadTemplate || false}
                        downloadTemplateLabel={f.downloadTemplateLabel || "Download Template"}
                        onDownloadTemplate={
                          f.onDownloadTemplate ? deps[f.onDownloadTemplate] : null
                        }
                      />
                    </MDBox>
                  );
                }

                if (f.fieldType === "autocomplete") {
                  return (
                    <AutocompleteField
                      key={f.id}
                      f={f}
                      data={data}
                      deps={deps}
                      hasError={hasError}
                      hasSuccess={hasSuccess}
                      errMsg={errMsg}
                      onFieldDataChange={onFieldDataChange}
                    />
                  );
                }

                if (f.fieldType === "dynamicList") {
                  const list = Array.isArray(data[f.id]) ? data[f.id] : [];

                  const handleUpdateRow = (index, fieldId, value) => {
                    const newList = [...list];
                    newList[index] = { ...newList[index], [fieldId]: value };
                    setField(f.id, newList);
                    f.columns.forEach((col) => {
                      if (col.id === fieldId) {
                        if (col.hasOwnProperty("onAfterValueUpdate")) {
                          col.onAfterValueUpdate(value, deps, data);
                        }
                      }
                    });
                  };

                  const handleAddRow = () => {
                    const newRow = { id: null };
                    f.columns.forEach((col) => {
                      newRow[col.id] = col.type === "number" ? 0 : "";
                    });
                    setField(f.id, [...list, newRow]);
                  };

                  const handleClearRows = () => {
                    setField(f.id, []);
                  };

                  const handleRemoveRow = (index) => {
                    const newList = list.filter((_, i) => i !== index);
                    setField(f.id, newList);
                  };

                  return (
                    <MDBox
                      key={f.id}
                      mb={3}
                      sx={{
                        width: "100%",
                        border: hasError ? "1px solid red" : "1px solid #eee",
                        p: 2,
                        borderRadius: 2,
                      }}
                    >
                      <MDTypography
                        variant="h6"
                        fontWeight="medium"
                        mb={2}
                        color={hasError ? "error" : "dark"}
                      >
                        {f.label}
                      </MDTypography>

                      {list.map((row, index) => {
                        const isProperObject =
                          row !== null && typeof row === "object" && !Array.isArray(row);
                        if (!isProperObject) {
                          console.error(`Row at index ${index} is not a proper object:`, row);
                          return null;
                        }

                        return (
                          <MDBox
                            key={index}
                            mb={2}
                            sx={{
                              width: "100%",
                              p: 2,
                              backgroundColor: "#fbfbfb",
                              borderRadius: "8px",
                              position: "relative",
                              border: "1px solid #f1eaea",
                            }}
                          >
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <MDTypography variant="caption" fontWeight="bold" color="text">
                                ENTRY #{index + 1}
                              </MDTypography>
                              <MDButton
                                variant="text"
                                color="error"
                                size="medium"
                                onClick={() => handleRemoveRow(index)}
                              >
                                <DeleteIcon fontSize="medium" />
                              </MDButton>
                            </MDBox>

                            <Grid
                              container
                              backgroundColor="#f8f9fa"
                              display="flex"
                              spacing={2}
                              alignItems="flex-end"
                            >
                              {f.columns.map((col) => {
                                const rawOptions =
                                  typeof col.options === "function"
                                    ? col.options(deps)
                                    : deps[col.optionsKey];
                                const colOptions = rawOptions || [];
                                const vKey = col.valueKey || "value";
                                const tKey = col.textValue || "label";

                                const currentValId = row[col.id];
                                const selectedOption =
                                  colOptions.find(
                                    (opt) => String(opt[vKey]) === String(currentValId)
                                  ) || null;
                                if (
                                  currentValId &&
                                  !selectedOption &&
                                  !col.loading &&
                                  typeof col.onSearch === "function"
                                ) {
                                  col.onSearch(currentValId, deps);
                                }
                                let returnComponent = null;
                                if (col.type === "autocomplete") {
                                  returnComponent = (
                                    <FormControl
                                      fullWidth
                                      variant="standard"
                                      sx={{
                                        width: col.width || "100%",
                                      }}
                                    >
                                      <DynamicAutocomplete
                                        col={col}
                                        row={row}
                                        index={index}
                                        handleUpdateRow={handleUpdateRow}
                                        deps={deps}
                                        vKey={vKey}
                                        tKey={tKey}
                                      />
                                      <MDBox height={26}>
                                        <MDTypography variant="caption" sx={col.sx}>
                                          {Object.hasOwn(col, "helperTextKey")
                                            ? col.helperTextVal(row[col.helperTextKey])
                                            : " "}
                                        </MDTypography>
                                      </MDBox>
                                    </FormControl>
                                  );
                                } else if (col.type === "select") {
                                  returnComponent = (
                                    <FormControl
                                      fullWidth
                                      variant="standard"
                                      sx={{
                                        width: "100%",
                                      }}
                                    >
                                      <InputLabel shrink sx={{ fontSize: "1rem" }}>
                                        {col.label}
                                      </InputLabel>
                                      <Select
                                        value={row[col.id] || ""}
                                        onChange={(e) =>
                                          handleUpdateRow(index, col.id, e.target.value)
                                        }
                                        sx={{ height: 26 }}
                                      >
                                        {colOptions.map((opt) => (
                                          <MenuItem key={opt[vKey]} value={opt[vKey]}>
                                            {opt[tKey]}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      <MDBox height={26}>
                                        <MDTypography variant="caption" sx={col.sx}>
                                          {Object.hasOwn(col, "helperTextKey")
                                            ? col.helperTextVal(row[col.helperTextKey])
                                            : " "}
                                        </MDTypography>
                                      </MDBox>
                                    </FormControl>
                                  );
                                } else if (col.type === "checkbox") {
                                  returnComponent = (
                                    <FormControl
                                      fullWidth
                                      variant="standard"
                                      sx={{
                                        width: "100%",
                                        margin: 0,
                                        padding: 0,
                                      }}
                                    >
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={row[col.id] === col.valueMap.y}
                                            onChange={(e) => {
                                              const val = e.target.checked
                                                ? col.valueMap.y
                                                : col.valueMap.n;
                                              handleUpdateRow(index, col.id, val);
                                            }}
                                          />
                                        }
                                        label={
                                          <MDTypography
                                            variant="button"
                                            fontWeight="regular"
                                            color="text"
                                          >
                                            {col.label}
                                          </MDTypography>
                                        }
                                      />
                                      <MDBox height={26}>
                                        <MDTypography variant="caption" sx={col.sx}>
                                          {Object.hasOwn(col, "helperTextKey")
                                            ? col.helperTextVal(row[col.helperTextKey])
                                            : " "}
                                        </MDTypography>
                                      </MDBox>
                                    </FormControl>
                                  );
                                } else {
                                  returnComponent = (
                                    <FormControl
                                      fullWidth
                                      variant="standard"
                                      sx={{
                                        width: "100%",
                                        margin: 0,
                                        padding: 0,
                                      }}
                                    >
                                      <MDInput
                                        label={col.label}
                                        type={col.type || "text"}
                                        value={row[col.id] || ""}
                                        onChange={(e) =>
                                          handleUpdateRow(index, col.id, e.target.value)
                                        }
                                        variant="standard"
                                        fullWidth
                                      />
                                      <MDBox height={26}>
                                        <MDTypography variant="caption" sx={col.sx}>
                                          {Object.hasOwn(col, "helperTextKey")
                                            ? col.helperTextVal(row[col.helperTextKey])
                                            : " "}
                                        </MDTypography>
                                      </MDBox>
                                    </FormControl>
                                  );
                                }

                                return (
                                  <Grid item xs={20} sm={4} key={col.id}>
                                    {returnComponent}
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </MDBox>
                        );
                      })}

                      {hasError && (
                        <MDTypography
                          variant="caption"
                          color="error"
                          sx={{ fontWeight: "bold", display: "block", mt: 1 }}
                        >
                          {errMsg}
                        </MDTypography>
                      )}

                      {!f.isFixedList && (
                        <MDBox mt={1}>
                          <MDButton variant="text" color="info" size="small" onClick={handleAddRow}>
                            + Add {f.label.split(" ").pop()}
                          </MDButton>
                          <MDButton
                            variant="text"
                            color="error"
                            size="small"
                            onClick={handleClearRows}
                          >
                            - Clear All
                          </MDButton>
                        </MDBox>
                      )}
                    </MDBox>
                  );
                }

                return null;
              })}
            </Grid>
          </MDBox>
        ))}
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" variant="text" onClick={onClose} disabled={saving}>
          Cancel
        </MDButton>
        <MDButton color="info" variant="gradient" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : submitButtonText || "Submit"}
        </MDButton>
        {deps.buttons &&
          deps.buttons.map((b) => {
            return (
              <MDButton
                key={b.index}
                color={b.color}
                variant={b.variant}
                onClick={() => handleSubmitFromOtherButtons(b.onClick)}
              >
                {`${b.label}`}
              </MDButton>
            );
          })}
      </DialogActions>
    </Dialog>
  );
}

SchemaDialogForm.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  schema: PropTypes.array.isRequired,
  initialRecord: PropTypes.object,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectOptionsMap: PropTypes.object,
  selectTextValueMap: PropTypes.object,
  deps: PropTypes.object,
  submitButtonText: PropTypes.string,
};

export default SchemaDialogForm;
